/**
 * The customer's drawn signature (M2-08, pass B): the strokes, whether there
 * is anything to sign with, and the PNG the API accepts.
 *
 * Kept apart from the canvas component because jsdom can draw nothing and
 * lay out nothing — every rule here is tested as plain logic, and the real
 * drawing was checked in a browser (web/CONVENTIONS.md, M2-08 pass B).
 *
 * ⚠️ The strokes are the source of truth, not the pixels. "Is the pad empty?"
 * is answered from them (a pixel scan would need a real canvas, and would call
 * a pad with one stray dot signed), and a clear or a resize redraws from them.
 */

/** A point in the canvas's own drawing units (see {@link PAD_WIDTH}), not CSS pixels. */
export interface Point {
  x: number;
  y: number;
}

export type Stroke = Point[];

/**
 * The pad's fixed drawing resolution. The canvas is styled to its container's
 * width (a phone's is ~340 CSS px) and pointer positions are scaled into these
 * units, so the PNG is always this size: well inside the API's 4096 px per
 * side, crisp at a phone's 3x density, and a few KB for a signature.
 */
export const PAD_WIDTH = 900;
export const PAD_HEIGHT = 300;

/** `EstimateAnswer.SignatureImageMaxBytes`: the decoded PNG, at most 128 KB. */
export const SIGNATURE_MAX_BYTES = 128 * 1024;

/** `SignatureImages`' IHDR rule: each side 1–4096 px. */
export const SIGNATURE_MAX_SIDE = 4096;

/** A typed signature, shown the way a signature reads — on the customer's page and the office's alike. */
export const TYPED_SIGNATURE_CLASS =
  "font-['Segoe_Script','Brush_Script_MT','Snell_Roundhand',cursive] text-2xl";

/** Ink width in drawing units — about 2.5 CSS px on a phone-width pad. */
export const INK_WIDTH = 6;

/**
 * Where a pointer is, in drawing units: the offset into the canvas's rendered
 * box, scaled from CSS pixels to {@link PAD_WIDTH} × {@link PAD_HEIGHT}.
 * A box with no size (not laid out yet) yields null rather than dividing by 0.
 */
export function toPadPoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  width = PAD_WIDTH,
  height = PAD_HEIGHT,
): Point | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const x = ((clientX - rect.left) * width) / rect.width;
  const y = ((clientY - rect.top) * height) / rect.height;
  // Pointer capture keeps reporting a finger that has slid off the pad; the
  // stroke stops at the edge instead of drawing outside the image.
  return { x: clamp(x, 0, width), y: clamp(y, 0, height) };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A new stroke started at `point`. Returns a new array; never mutates. */
export function startStroke(strokes: readonly Stroke[], point: Point): Stroke[] {
  return [...strokes, [point]];
}

/**
 * `point` added to the stroke in progress. A point identical to the last one
 * (a pointer that reports without moving) is dropped, so it cannot make a
 * tap look like a line.
 */
export function extendStroke(strokes: readonly Stroke[], point: Point): Stroke[] {
  const last = strokes[strokes.length - 1];
  if (!last) return startStroke(strokes, point);
  const previous = last[last.length - 1];
  if (previous && previous.x === point.x && previous.y === point.y) return [...strokes];
  return [...strokes.slice(0, -1), [...last, point]];
}

/**
 * Whether the strokes amount to something to sign with: at least one stroke
 * that MOVED. A tap on its own — a finger brushing the pad on the way to the
 * button — is a dot, not a signature, and is drawn but not counted.
 */
export function hasInk(strokes: readonly Stroke[]): boolean {
  return strokes.some((stroke) => stroke.length >= 2);
}

/** The part of a 2D context the pad draws with — a real one, or a test's recorder. */
export type PadContext = Pick<
  CanvasRenderingContext2D,
  | 'clearRect'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'stroke'
  | 'arc'
  | 'fill'
  | 'lineWidth'
  | 'lineCap'
  | 'lineJoin'
  | 'strokeStyle'
  | 'fillStyle'
>;

/**
 * Clears the pad and draws every stroke again, in near-black ink on a
 * transparent background (the office shows it on a white panel). A one-point
 * stroke is drawn as a dot, so a tap is visible even though it does not count.
 */
export function drawStrokes(
  context: PadContext,
  strokes: readonly Stroke[],
  width = PAD_WIDTH,
  height = PAD_HEIGHT,
): void {
  context.clearRect(0, 0, width, height);
  context.lineWidth = INK_WIDTH;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.strokeStyle = '#111827';
  context.fillStyle = '#111827';
  for (const stroke of strokes) {
    const [first, ...rest] = stroke;
    if (!first) continue;
    context.beginPath();
    if (rest.length === 0) {
      context.arc(first.x, first.y, INK_WIDTH / 2, 0, Math.PI * 2);
      context.fill();
      continue;
    }
    context.moveTo(first.x, first.y);
    for (const point of rest) context.lineTo(point.x, point.y);
    context.stroke();
  }
}

/** Why a drawn signature could not become a PNG the API will take. */
export type ExportProblem = 'empty' | 'tooLarge' | 'failed';

export type ExportResult = { ok: true; base64: string } | { ok: false; problem: ExportProblem };

/** The canvas the export needs: its size and `toBlob`. */
export type ExportCanvas = Pick<HTMLCanvasElement, 'width' | 'height' | 'toBlob'>;

/**
 * The drawn signature as the API's `signatureImage`: base64 of a PNG, with no
 * `data:` prefix. Refused here, before it is sent, when there is no ink, when
 * the canvas is larger than the API allows on a side, or when the PNG is over
 * {@link SIGNATURE_MAX_BYTES} — the customer is told at once instead of after
 * a round trip on a slow phone. The API still checks all of it.
 */
export async function exportSignaturePng(
  canvas: ExportCanvas,
  strokes: readonly Stroke[],
): Promise<ExportResult> {
  if (!hasInk(strokes)) return { ok: false, problem: 'empty' };
  if (!withinSides(canvas.width, canvas.height)) return { ok: false, problem: 'tooLarge' };

  const blob = await new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/png');
    } catch {
      // A tainted or zero-sized canvas throws rather than calling back.
      resolve(null);
    }
  });
  if (!blob || blob.type !== 'image/png') return { ok: false, problem: 'failed' };
  if (blob.size > SIGNATURE_MAX_BYTES) return { ok: false, problem: 'tooLarge' };
  return { ok: true, base64: await blobToBase64(blob) };
}

/** Each side 1–{@link SIGNATURE_MAX_SIDE} px, as the API's IHDR check reads it. */
export function withinSides(width: number, height: number): boolean {
  return width >= 1 && height >= 1 && width <= SIGNATURE_MAX_SIDE && height <= SIGNATURE_MAX_SIDE;
}

/**
 * A blob's bytes as base64. In slices, because `String.fromCharCode(...bytes)`
 * over a whole 128 KB array overflows the argument limit in some engines.
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const SLICE = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += SLICE) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + SLICE));
  }
  return btoa(binary);
}
